// FormulaInput component for entering and validating formulas
import { useEffect, useMemo, useState } from 'react';
import { Formik, type FormikProps } from 'formik';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/theme-monokai';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  Subtitle,
  Select,
  Button,
  Input,
  Switch,
  Paragraph,
  HelperText,
} from '@yaradigitallabs/ahua-react';
import type { CreateSearchTagParams } from '@/features/tags/types/tags.types';
import {
  selectSearchTags,
  selectSearchTagsLoading,
} from '@/features/tags/store/tags.selectors';
import { clearSearchFilters } from '@/features/tags/store/tags.slice';
import { getSearchTags } from '@/features/tags/store/tags.thunk';
import { TagsTable } from '@/features/tags/component/TagsTable';
import { copyToClipboard } from '@/utils/clipboard';
import {
  FormulaMateFixLink,
  FormulaMateReviewLink,
} from '@/features/formulaMate/FormulaMateContext';
import {
  selectValidationLoading,
  selectValidationError,
  selectValidationIsValid,
} from '../store/createSoftTag.selectors';
import { validateCode } from '../store/createSoftTag.thunk';
import { resetValidation as resetValidationAction } from '../store/createSoftTag.slice';

import type { SoftTagState } from '../utils/types';
import { columns } from './CreateTagsColumns';
import { filterSites } from '../utils/helpers';
import {
  selectAssetHierarchy,
  selectAssetHierarchyLoading,
} from '@/features/assetHierarchy/store/assetHierarchy.selectors';
import { Colors } from '../../../../public/GlobalTokens';
import { FormulaGuideline } from './Guidelines/FormulaGuideline';
import { SearchSoftTagGuideline } from './Guidelines/SearchSoftTagGuideline';
import { AlertDialogBox } from './AlertDialogBox';
import {
  cofTemplate,
  parsableTemplate,
  templateOptions,
} from '../utils/constants';

interface FormulaInputProps {
  handleConfirm: (values: { executionCode: string }) => void;
  handleBack: () => void;
  softTagDetails: SoftTagState | null;
  isEditMode: boolean;
}

const resetFilters = {
  site: '',
  area: '',
  production_unit: '',
  tag_name: '',
};

// Simple formula input with validation and error display
const FormulaInput = ({
  handleConfirm,
  handleBack,
  softTagDetails,
}: FormulaInputProps) => {
  const dispatch = useAppDispatch();
  const [filters, setFilters] = useState<CreateSearchTagParams>(resetFilters);
  const [templateFilter, setTemplateFilter] = useState<string>('');
  const [template, setTemplate] = useState<string>('');
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showAlertDialog, setShowAlertDialog] = useState<boolean>(false);
  const loading = useAppSelector(selectSearchTagsLoading);
  const searchTags = useAppSelector(selectSearchTags);
  const validationLoading = useAppSelector(selectValidationLoading);
  const validationError = useAppSelector(selectValidationError);
  const validationIsValid = useAppSelector(selectValidationIsValid);

  const assetHierarchy = useAppSelector(selectAssetHierarchy);
  const assetHierarchyLoading = useAppSelector(selectAssetHierarchyLoading);
  const { filteredSites, filteredAreas, filteredUnits } = useMemo(() => {
    return filterSites(
      assetHierarchy,
      filters.site,
      filters.area,
      filters.production_unit
    );
  }, [assetHierarchy, filters.site, filters.area, filters.production_unit]);

  const showAssetHierarchyWarning = !(
    (Array.isArray(assetHierarchy) && assetHierarchy.length > 0) ||
    assetHierarchyLoading
  );

  // Initial form state
  const state = {
    executionCode: softTagDetails?.executionCode || '',
    schedule: softTagDetails?.schedule || '',
  };

  useEffect(() => {
    if (templateFilter) {
      switch (templateFilter) {
        case 'COF':
          setTemplate(cofTemplate);
          break;
        case 'PARSABLE':
          setTemplate(parsableTemplate);
          break;
      }
    }
  }, [templateFilter]);

  // Handles filter dropdown/input changes
  const handleFilterChange = (
    filterType: keyof CreateSearchTagParams,
    selectedValues: string
  ) => {
    const updatedFilters = {
      ...filters,
      [filterType]: selectedValues,
    };
    setFilters(updatedFilters);
  };

  // Handles reset button click, clears filters and reloads tags
  const handleReset = () => {
    const resetFilters: CreateSearchTagParams = {
      tag_name: '',
      site: '',
      area: '',
      production_unit: '',
    };
    setFilters(resetFilters);
    dispatch(clearSearchFilters());
  };

  const handleTagSearch = () => {
    if (
      (filters.tag_name !== '' && filters.tag_name.length >= 3) ||
      filters.tag_name === ''
    ) {
      const payload = {
        tag_name: filters.tag_name,
        site: filters.site,
        area: filters.area,
        production_unit: filters.production_unit,
      };
      dispatch(getSearchTags(payload));
    }
  };

  const handleBackClick = () => {
    handleReset();
    handleBack();
  };

  const handleTemplateChange = useMemo(
    () => (selectedTemplate: string, setFieldValue) => {
      setTemplateFilter(selectedTemplate);
      setFieldValue(
        'executionCode',
        selectedTemplate === 'COF' ? cofTemplate : parsableTemplate
      );
    },
    []
  );

  useEffect(() => {
    if (filters.tag_name !== '' && filters.tag_name.length >= 3) {
      setHasActiveFilters(true);
    } else {
      setHasActiveFilters(false);
    }
  }, [filters]);

  // Reset validation when component mounts or execution code changes
  useEffect(() => {
    dispatch(resetValidationAction());
  }, [dispatch]);

  // Reset validation when execution code changes
  useEffect(() => {
    dispatch(resetValidationAction());
  }, [dispatch, softTagDetails?.executionCode]);

  const isSiteSelectDisabled = useMemo(
    () => assetHierarchyLoading || filteredSites.length === 0,
    [assetHierarchyLoading, filteredSites.length]
  );
  const isAreaSelectDisabled = useMemo(
    () => assetHierarchyLoading || filteredAreas.length === 0,
    [assetHierarchyLoading, filteredAreas.length]
  );
  const isUnitSelectDisabled = useMemo(
    () => assetHierarchyLoading || filteredUnits.length === 0,
    [assetHierarchyLoading, filteredUnits.length]
  );

  const onValidateHandler = async (executionCode: string) => {
    // Reset previous validation state
    dispatch(resetValidationAction());

    try {
      // Validate the code
      await dispatch(validateCode({ code: executionCode })).unwrap();
    } catch (error) {
      // Error is handled by the rejected case in the slice
      console.error('Validation failed:', error);
    }
  };

  const onSubmitHandler = async values => {
    // If code is empty, allow proceeding without validation
    if (!values.executionCode.trim()) {
      setShowAlertDialog(true);
      return;
    }

    // If scheduling is 'Never' (value is ''), allow proceeding regardless of validation
    if (!values.schedule) {
      handleReset();
      handleConfirm(values);
      return;
    }

    // Otherwise, require validation
    if (validationIsValid === true) {
      handleReset();
      handleConfirm(values);
    }
    // If not valid, do not proceed (user must validate)
  };

  const onAddFormulaLater = () => {
    setShowAlertDialog(false);
    handleReset();
    handleConfirm({ executionCode: '' });
  };

  const onAddFormulaNow = () => {
    setShowAlertDialog(false);
  };

  const onSetOpenAlertDialogBox = (open: boolean) => {
    setShowAlertDialog(open);
  };

  return (
    <div>
      <Formik
        validateOnMount={true}
        validateOnBlur={true}
        validateOnChange={true}
        initialValues={{
          ...state,
        }}
        onSubmit={values => {
          onSubmitHandler(values);
        }}
      >
        {({
          handleSubmit,
          values,
          setFieldValue,
        }: FormikProps<{ executionCode: string; schedule: string }>) => (
          <form
            className="w-full flex flex-col gap-4 mb-4"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-row gap-8 w-full h-[500px]">
              {/* Left section: tag selection, 40% width */}
              <div className="w-[50%] min-w-[200px]">
                <div className="flex items-center gap-1">
                  <Subtitle>Select tag(s)</Subtitle>
                  <SearchSoftTagGuideline />
                </div>

                {/* Production site, area, and unit selects */}
                <div className="flex flex-col justify-between gap-2 mt-2">
                  {/* Search input for soft tag ID or name */}
                  <Input
                    iconLeading="Search"
                    label="Search for tag name"
                    size={'n'}
                    value={filters.tag_name}
                    onChange={e =>
                      handleFilterChange('tag_name', e.target.value as string)
                    }
                    cover="fill"
                  />
                  <Switch
                    label="Show more filters"
                    checked={showFilters}
                    onCheckedChange={() => setShowFilters(!showFilters)}
                  />
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      showFilters
                        ? 'max-h-32 opacity-100 transform translate-y-0'
                        : 'max-h-0 opacity-0 transform -translate-y-2'
                    }`}
                    style={{
                      transitionProperty: 'max-height, opacity, transform',
                    }}
                  >
                    <div className="flex justify-between gap-2 pt-2 pb-2">
                      <Select
                        value={filters.site}
                        ariaLabel="Select element"
                        cover="fill"
                        items={filteredSites}
                        label="Production site"
                        loadingText="Loading..."
                        position="popper"
                        selectContentAutoHeight
                        size="n"
                        onChange={value => handleFilterChange('site', value)}
                        css={{ width: '100%' }}
                        disabled={isSiteSelectDisabled}
                      />
                      <Select
                        value={filters.area}
                        ariaLabel="Select element"
                        cover="fill"
                        items={filteredAreas}
                        label="Area"
                        loadingText="Loading..."
                        position="popper"
                        selectContentAutoHeight
                        size="n"
                        onChange={value => handleFilterChange('area', value)}
                        css={{ width: '100%' }}
                        disabled={isAreaSelectDisabled}
                      />
                      <Select
                        value={filters.production_unit}
                        ariaLabel="Select element"
                        cover="fill"
                        items={filteredUnits}
                        label="Production unit"
                        loadingText="Loading..."
                        position="popper"
                        selectContentAutoHeight
                        size="n"
                        onChange={value =>
                          handleFilterChange('production_unit', value)
                        }
                        css={{ width: '100%' }}
                        disabled={isUnitSelectDisabled}
                      />
                    </div>
                    {showAssetHierarchyWarning && (
                      <HelperText colorConcept="destructive" icon="Info">
                        The site,area and unit dropdowns are unavailable due to
                        some issues in asset hierarchy api. Please try again
                        later.
                      </HelperText>
                    )}
                  </div>
                  <div
                    className={`${searchTags.length > 0 ? 'flex justify-between items-end' : ''}`}
                  >
                    {searchTags.length > 0 && (
                      <Paragraph
                        maxLineCount={1}
                        size="s"
                        style={{ color: Colors.colorType.brand.contrast }}
                      >
                        Search results: {searchTags.length}
                      </Paragraph>
                    )}
                    <div className="flex gap-3 justify-end">
                      {/* Reset Button */}
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={handleReset}
                        colorConcept="brand"
                        disabled={!hasActiveFilters || loading}
                        type="button"
                      >
                        Reset
                      </Button>

                      {/* Search Button */}
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={handleTagSearch}
                        colorConcept="brand"
                        disabled={!hasActiveFilters || loading}
                        type="button"
                      >
                        Search
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="mt-2 overflow-scroll max-h-[290px]">
                  <TagsTable
                    hasActiveFilter={hasActiveFilters}
                    columns={columns}
                    tags={searchTags}
                    loading={loading}
                  />
                </div>
              </div>
              {/* Right section: formula editor, 60% width */}
              <div className="w-[50%] min-w-[300px]">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-1">
                      <Subtitle>Formula</Subtitle>
                      <FormulaGuideline />
                    </div>
                    <Select
                      value={templateFilter}
                      ariaLabel="Select template"
                      cover="fill"
                      items={templateOptions}
                      label="Template"
                      loadingText="Loading..."
                      position="popper"
                      selectContentAutoHeight
                      size="xs"
                      onChange={value =>
                        handleTemplateChange(value, setFieldValue)
                      }
                      css={{ width: '25%' }}
                    />
                  </div>
                  <AceEditor
                    mode="python"
                    theme="monokai"
                    value={values.executionCode || template}
                    highlightActiveLine={true}
                    onChange={value => {
                      setFieldValue('executionCode', value);
                      // Reset validation when code changes, but only if schedule is not 'Never'
                      if (softTagDetails?.schedule) {
                        dispatch(resetValidationAction());
                      }
                    }}
                    width="100%"
                    height="450px"
                    name="formula-code"
                    editorProps={{ $blockScrolling: true }}
                    setOptions={{
                      showGutter: true,
                      wrap: true,
                      vScrollBarAlwaysVisible: true, // vertical scrollbar
                      hScrollBarAlwaysVisible: true,
                    }}
                    commands={[
                      {
                        name: 'customCopy',
                        bindKey: { win: 'Ctrl-C', mac: 'Cmd-C' },
                        exec(editor) {
                          const selectedText = editor.getSelectedText();
                          if (selectedText) {
                            copyToClipboard(selectedText).catch(console.error);
                          }
                        },
                      },
                    ]}
                  />
                  {/* Validation feedback display (moved above buttons for layout) */}
                  <div style={{ minHeight: 48, marginBottom: 8 }}>
                    {validationError && (
                      <div className="mt-2" style={{ marginBottom: 8 }}>
                        <HelperText
                          colorConcept="destructive"
                          icon="Info"
                          style={{
                            fontSize: '1.1rem',
                            fontWeight: 500,
                            background: '#fff',
                            zIndex: 2,
                            position: 'relative',
                            padding: '8px 12px',
                            borderRadius: 6,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          }}
                        >
                          Validation Error: {validationError}{' '}
                          <FormulaMateFixLink code={values.executionCode} />
                        </HelperText>
                      </div>
                    )}
                    {validationIsValid === true && !validationError && (
                      <div className="mt-2" style={{ marginBottom: 8 }}>
                        <HelperText
                          colorConcept="positive"
                          icon="Info"
                          style={{
                            color: '#16a34a',
                            fontSize: '1.3rem',
                            fontWeight: 700,
                            background: '#f0fdf4',
                            zIndex: 2,
                            position: 'relative',
                            padding: '10px 16px',
                            borderRadius: 8,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          }}
                        >
                          ✓ Syntax check passed. Check Formula Logic?{' '}
                          <FormulaMateReviewLink code={values.executionCode} />
                        </HelperText>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Navigation buttons */}
            <div
              className="flex flex-row align-items justify-end gap-3"
              style={{ marginTop: 32, zIndex: 1, position: 'relative' }}
            >
              <Button
                variant="primary"
                css={{ width: '70px' }}
                size="s"
                onClick={handleBackClick}
                colorConcept="inverse"
                type="button"
              >
                Back
              </Button>
              <Button
                variant="outline"
                css={{ width: '140px' }}
                size="s"
                onClick={() => onValidateHandler(values.executionCode)}
                loading={validationLoading}
                disabled={validationLoading || !values.executionCode.trim()}
                type="button"
              >
                {validationLoading ? 'Validating...' : 'Validate'}
              </Button>
              <Button
                css={{ width: '200px' }}
                size="s"
                type="submit"
                disabled={
                  validationLoading ||
                  (!!values.schedule &&
                    values.executionCode.trim() &&
                    validationIsValid !== true)
                }
              >
                Confirm and proceed
              </Button>
            </div>
          </form>
        )}
      </Formik>
      <AlertDialogBox
        open={showAlertDialog}
        onSetOpenAlertDialogBox={onSetOpenAlertDialogBox}
        onAddLater={onAddFormulaLater}
        onAddNow={onAddFormulaNow}
        shouldAddFormula={!!softTagDetails?.schedulingInterval}
      />
    </div>
  );
};

export default FormulaInput;
